import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { bdeseService } from '../services/bdese.service';
import type {
  PointDeDonnees,
  RapportConsulte,
  Referentiel,
  RubriqueReferentiel,
} from '../types';

/** Ce qu'on peut demander à voir. */
type Filtre = 'tous' | 'a_saisir' | 'calcules';

/**
 * La saisie — ce que l'entreprise complète, et que la paie ne donnera jamais.
 *
 * CE QUE CET ÉCRAN RÉSOUT. Sans lui, seuls les indicateurs calculés depuis la
 * paie se remplissaient : effectifs, absentéisme, masse salariale. Tout le
 * reste — capitaux propres, aides publiques, consommation d'énergie — restait
 * vide, et une BDESE incomplète coûte 7 500 € à l'entreprise.
 *
 * LA LISTE VIENT DU SERVEUR, JAMAIS D'ICI. Les rubriques et les indicateurs
 * applicables sont déduits de la catégorie de seuil du rapport
 * (`GET /bdese/reports/:uuid/referentiel`). Porter une copie de la liste légale
 * dans le navigateur, c'est se garantir une divergence au premier décret — et
 * une divergence que personne ne verrait avant un contrôle.
 *
 * LA FORME VIENT DE LA MAQUETTE C4 (`docs/chantier-c4/ecrans/bdese-saisie.html`)
 * : des rubriques repliables, l'avancement lisible SANS déplier, et un filtre
 * par source. Quatre-vingt-onze champs dépliés d'un coup, personne ne les
 * remplit.
 *
 * CE QUI EST MONTRÉ EN PLUS DU CHAMP. L'article, toujours — c'est lui qui rend
 * le chiffre opposable. Et la condition d'applicabilité quand le texte dit « le
 * cas échéant » : sans elle, une entreprise croit avoir une lacune là où elle
 * n'est pas concernée.
 */
export function BdeseSaisiePage() {
  const { folderUuid = '', reportUuid = '' } = useParams();
  const [referentiel, setReferentiel] = useState<Referentiel | null>(null);
  const [donnees, setDonnees] = useState<RapportConsulte | null>(null);
  const [chargement, setChargement] = useState(true);
  const [autorise, setAutorise] = useState(true);
  const [ouvertes, setOuvertes] = useState<Set<string>>(new Set());
  const [filtre, setFiltre] = useState<Filtre>('tous');
  const [recherche, setRecherche] = useState('');
  const [enCours, setEnCours] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [erreurChargement, setErreurChargement] = useState<string | null>(null);

  const charger = useCallback(async () => {
    if (!reportUuid) return;
    setChargement(true);
    setErreurChargement(null);
    try {
      const droits = await bdeseService.lireModalites(folderUuid);
      if (!droits.modifiable) {
        setAutorise(false);
        setReferentiel(null);
        setDonnees(null);
        return;
      }
      const [ref, consulte] = await Promise.all([
        bdeseService.referentiel(reportUuid),
        bdeseService.consulter(reportUuid),
      ]);
      setReferentiel(ref);
      setDonnees(consulte);
      setAutorise(true);
    } catch (e: unknown) {
      const statut = (e as { response?: { status?: number } })?.response
        ?.status;
      setAutorise(statut !== 401 && statut !== 403);
      setReferentiel(null);
      setDonnees(null);
      if (statut !== 401 && statut !== 403) {
        setErreurChargement(
          'Les informations nécessaires à la saisie ne sont pas disponibles pour le moment.'
        );
      }
    } finally {
      setChargement(false);
    }
  }, [folderUuid, reportUuid]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /** Ce qui est déjà renseigné, retrouvé par le code de l'indicateur. */
  const parCode = useMemo(() => {
    const m = new Map<string, PointDeDonnees>();
    for (const p of donnees?.pointsDeDonnees ?? []) {
      if (p.anneePeriode === donnees?.rapport.annee) {
        m.set(p.indicateurCode, p);
      }
    }
    return m;
  }, [donnees]);

  const rubriquesVisibles = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return (referentiel?.rubriques ?? [])
      .map(r => ({
        ...r,
        indicateurs: r.indicateurs.filter(i => {
          const point = parCode.get(i.code);
          const rempli = pointEstRenseigne(point);
          if (
            filtre === 'a_saisir' &&
            (rempli || i.alimentation === 'automatique')
          )
            return false;
          if (filtre === 'calcules' && i.alimentation !== 'automatique')
            return false;
          if (!q) return true;
          return (
            i.libelle.toLowerCase().includes(q) ||
            i.cheminArticle.toLowerCase().includes(q)
          );
        }),
      }))
      .filter(r => r.indicateurs.length > 0);
  }, [referentiel, parCode, filtre, recherche]);

  const enregistrer = async (
    code: string,
    valeur: string,
    typeDonnee: string
  ) => {
    const valeurNormalisee = valeur.trim();
    if (!valeurNormalisee) {
      setMessage('Saisissez une valeur avant de l’enregistrer.');
      return;
    }
    const valeurNumerique = Number(valeurNormalisee.replace(',', '.'));
    if (
      (typeDonnee === 'nombre' || typeDonnee === 'montant') &&
      !Number.isFinite(valeurNumerique)
    ) {
      setMessage('Saisissez un nombre valide.');
      return;
    }
    setEnCours(code);
    setMessage(null);
    try {
      await bdeseService.saisir(reportUuid, {
        indicateurCode: code,
        anneePeriode: donnees?.rapport.annee ?? new Date().getFullYear(),
        typePeriode: 'realise',
        regimeValeur: 'chiffree',
        ...(typeDonnee === 'nombre' || typeDonnee === 'montant'
          ? { valeurNumerique }
          : { valeurTexte: valeurNormalisee }),
      });
      await charger();
    } catch (e: unknown) {
      const corps = (e as { response?: { data?: { message?: string } } })
        ?.response?.data;
      setMessage(corps?.message ?? "Cette valeur n'a pas pu être enregistrée.");
    } finally {
      setEnCours(null);
    }
  };

  if (!autorise) {
    return (
      <div className="max-w-lg rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <p className="text-sm text-neutral-600">
          La saisie de cet exercice ne vous est pas ouverte.
        </p>
        <Link
          to={`/bdese/${folderUuid}/exercice/${reportUuid}`}
          className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-950"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à l&apos;exercice
        </Link>
      </div>
    );
  }
  if (chargement) {
    return <p className="py-8 text-sm text-neutral-500">Chargement…</p>;
  }
  if (erreurChargement) {
    return (
      <div className="py-8" role="alert">
        <p className="text-sm text-red-800">{erreurChargement}</p>
        <button
          type="button"
          onClick={() => void charger()}
          className="focus-visible:ring-primary-600 mt-3 min-h-11 rounded-lg px-3 text-sm font-medium text-neutral-700 underline decoration-neutral-300 underline-offset-4 hover:text-neutral-950 focus-visible:ring-2 focus-visible:outline-none"
        >
          Réessayer
        </button>
      </div>
    );
  }
  if (!referentiel || !donnees) return null;

  // Sous cinquante salariés, AUCUNE base n'est due (L2312-18 vise les
  // entreprises d'au moins cinquante salariés). Afficher une liste vide
  // laisserait croire à un écran cassé, ou pire, à une obligation non remplie.
  if (referentiel.categorieSeuil === 'moins_de_50') {
    return (
      <div>
        <Link
          to={`/bdese/${folderUuid}/exercice/${reportUuid}`}
          className="mb-4 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à l&apos;exercice
        </Link>
        <h1 className="text-2xl font-semibold text-neutral-900">
          Rien à compléter sur cet exercice
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-neutral-600">
          L&apos;effectif de cette entreprise est inférieur à cinquante salariés
          : elle n&apos;est pas tenue de constituer une base économique et
          sociale. L&apos;effectif est calculé par le serveur selon L1111-2, et
          la catégorie en est déduite — elle ne se choisit pas.
        </p>
      </div>
    );
  }

  return (
    <div>
      <Link
        to={`/bdese/${folderUuid}/exercice/${reportUuid}`}
        className="mb-4 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour à l&apos;exercice
      </Link>

      <h1 className="text-2xl font-semibold text-neutral-900">
        Compléter l&apos;exercice {donnees.rapport.annee}
      </h1>
      <p className="mt-1 mb-4 text-sm text-neutral-500">
        Ce que la paie ne fournit pas se renseigne ici. Les indicateurs calculés
        sont affichés en lecture : ils se remplissent tout seuls.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(
          [
            ['tous', 'Tous'],
            ['a_saisir', 'À renseigner'],
            ['calcules', 'Calculés'],
          ] as const
        ).map(([cle, libelle]) => (
          <button
            key={cle}
            type="button"
            onClick={() => setFiltre(cle)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              filtre === cle
                ? 'text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
            style={
              filtre === cle
                ? { backgroundColor: 'var(--marque-accent)' }
                : undefined
            }
          >
            {libelle}
          </button>
        ))}
        <div className="relative ml-auto min-w-[16rem] flex-1">
          <Search className="absolute top-2.5 left-3 h-4 w-4 text-neutral-400" />
          <input
            type="search"
            value={recherche}
            onChange={e => setRecherche(e.target.value)}
            placeholder="Chercher un indicateur ou un article"
            className="w-full rounded-xl border py-2 pr-3 pl-9 text-base shadow-sm placeholder:text-neutral-400/50 focus:ring-2 focus:outline-none sm:text-sm"
            style={{
              borderColor: 'var(--marque-trait)',
              outlineColor: 'var(--marque-accent)',
            }}
          />
        </div>
      </div>

      {message && (
        <p
          className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-800"
          role="status"
        >
          {message}
        </p>
      )}

      <div className="shadow-soft overflow-hidden rounded-xl border border-neutral-200 bg-white">
        {rubriquesVisibles.length === 0 ? (
          <p className="p-6 text-sm text-neutral-500">
            Rien à afficher sur ce filtre.
          </p>
        ) : (
          rubriquesVisibles.map(r => (
            <RubriqueRepliable
              key={r.code}
              rubrique={r}
              parCode={parCode}
              ouverte={ouvertes.has(r.code)}
              enCours={enCours}
              surBascule={() =>
                setOuvertes(s => {
                  const n = new Set(s);
                  if (n.has(r.code)) n.delete(r.code);
                  else n.add(r.code);
                  return n;
                })
              }
              surEnregistrer={enregistrer}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Une rubrique : son avancement se lit SANS la déplier.
 *
 * C'est la règle qui rend l'écran praticable. Quatre-vingt-onze champs ouverts
 * d'un coup ne se remplissent pas ; « 3 sur 7 renseignés » dit où aller.
 */
function RubriqueRepliable({
  rubrique,
  parCode,
  ouverte,
  enCours,
  surBascule,
  surEnregistrer,
}: {
  rubrique: RubriqueReferentiel;
  parCode: Map<string, PointDeDonnees>;
  ouverte: boolean;
  enCours: string | null;
  surBascule: () => void;
  surEnregistrer: (code: string, valeur: string, type: string) => Promise<void>;
}) {
  const total = rubrique.indicateurs.length;
  const remplis = rubrique.indicateurs.filter(i => {
    const p = parCode.get(i.code);
    return pointEstRenseigne(p);
  }).length;

  return (
    <div className="border-b border-neutral-100 last:border-b-0">
      <button
        type="button"
        onClick={surBascule}
        aria-expanded={ouverte}
        className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left hover:bg-neutral-50"
      >
        <span className="flex items-center gap-2">
          {ouverte ? (
            <ChevronDown className="h-4 w-4 text-neutral-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-neutral-400" />
          )}
          <span className="text-sm font-medium text-neutral-900">
            {rubrique.libelle}
          </span>
        </span>
        <span className="flex items-center gap-3">
          <span
            className={`text-xs ${remplis === total ? 'text-green-700' : 'text-neutral-500'}`}
          >
            {remplis} / {total} renseignés
          </span>
        </span>
      </button>

      {ouverte && (
        <div className="bg-neutral-50/60 px-5 pb-4">
          <details className="py-2 text-xs text-neutral-500">
            <summary className="inline-flex min-h-8 cursor-pointer items-center font-medium hover:text-neutral-800">
              Fondement légal
            </summary>
            <p className="border-l-2 border-neutral-200 pl-3">
              {rubrique.cheminArticle}
            </p>
          </details>
          {rubrique.indicateurs.map(i => (
            <ChampIndicateur
              key={i.code}
              indicateur={i}
              point={parCode.get(i.code)}
              enCours={enCours === i.code}
              surEnregistrer={surEnregistrer}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Un indicateur : calculé (en lecture) ou à renseigner (avec son champ). */
function ChampIndicateur({
  indicateur,
  point,
  enCours,
  surEnregistrer,
}: {
  indicateur: RubriqueReferentiel['indicateurs'][number];
  point?: PointDeDonnees;
  enCours: boolean;
  surEnregistrer: (code: string, valeur: string, type: string) => Promise<void>;
}) {
  const automatique = indicateur.alimentation === 'automatique';
  const champId = `bdese-indicateur-${indicateur.code.replace(/[^a-z0-9_-]/gi, '-')}`;
  const valeur = valeurDuPoint(point);
  const valeurExistante =
    valeur === null || valeur === undefined
      ? ''
      : typeof valeur === 'object'
        ? JSON.stringify(valeur)
        : String(valeur);
  const [saisie, setSaisie] = useState(valeurExistante);

  useEffect(() => {
    setSaisie(valeurExistante);
  }, [valeurExistante]);

  return (
    <div className="border-t border-neutral-200/70 py-3 first:border-t-0">
      <div>
        <label htmlFor={champId} className="text-sm text-neutral-800">
          {indicateur.libelle}
          {indicateur.unite ? (
            <span className="text-neutral-400"> ({indicateur.unite})</span>
          ) : null}
        </label>
      </div>

      {/* « Le cas échéant » du texte : sans cette phrase, une entreprise croit
          avoir une lacune là où elle n'est pas concernée. */}
      {indicateur.conditionnel && indicateur.conditionApplicabilite && (
        <p className="mt-0.5 text-xs text-neutral-400">
          Concerne uniquement : {indicateur.conditionApplicabilite}
        </p>
      )}

      {automatique ? (
        <p className="mt-1 text-sm text-neutral-500">
          {valeurAutomatiqueLisible(point, indicateur.unite)}
        </p>
      ) : (
        <div className="mt-1.5 flex gap-2">
          <input
            id={champId}
            type="text"
            value={saisie}
            onChange={e => setSaisie(e.target.value)}
            placeholder="Valeur"
            maxLength={3000}
            className="min-w-0 flex-1 rounded-lg border bg-white px-3 py-2 text-base placeholder:text-neutral-400/50 focus:ring-2 focus:outline-none sm:text-sm"
            style={{
              borderColor: 'var(--marque-trait)',
              outlineColor: 'var(--marque-accent)',
            }}
          />
          <button
            type="button"
            disabled={enCours || saisie.trim() === valeurExistante.trim()}
            onClick={() =>
              void surEnregistrer(
                indicateur.code,
                saisie.trim(),
                indicateur.typeDonnee
              )
            }
            className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-30"
            style={{
              backgroundColor: 'var(--marque-accent)',
              color: 'var(--marque-sur-accent)',
            }}
          >
            Enregistrer
          </button>
        </div>
      )}
    </div>
  );
}

function valeurAutomatiqueLisible(
  point: PointDeDonnees | undefined,
  unite?: string
): string {
  if (!point) return 'Se remplira au calcul, depuis la paie.';
  if (point.mention) return point.mention;
  if (point.motifIndisponible) {
    return `Non renseigné — ${point.motifIndisponible}`;
  }
  const valeur = valeurDuPoint(point);
  if (typeof valeur === 'number') {
    return `${valeur.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}${unite ? ` ${unite}` : ''}`;
  }
  if (typeof valeur === 'string') return valeur;
  if (valeur && typeof valeur === 'object' && !Array.isArray(valeur)) {
    const total = (valeur as Record<string, unknown>).total;
    if (typeof total === 'number') {
      return `${total.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}${unite ? ` ${unite}` : ''}`;
    }
    const elements = compterValeursSimples(valeur);
    if (elements > 0) {
      return `${elements} élément${elements > 1 ? 's' : ''} calculé${elements > 1 ? 's' : ''} depuis la paie.`;
    }
  }
  return 'Calculé depuis la paie.';
}

function compterValeursSimples(valeur: unknown, profondeur = 0): number {
  if (valeur === null || valeur === undefined || profondeur > 5) return 0;
  if (['string', 'number', 'boolean'].includes(typeof valeur)) return 1;
  if (Array.isArray(valeur)) {
    return valeur.reduce(
      (total, element) =>
        total + compterValeursSimples(element, profondeur + 1),
      0
    );
  }
  return Object.values(valeur as Record<string, unknown>).reduce<number>(
    (total, enfant) => total + compterValeursSimples(enfant, profondeur + 1),
    0
  );
}

function valeurDuPoint(point?: PointDeDonnees): unknown {
  if (!point) return null;
  if (point.valeurNumerique !== null && point.valeurNumerique !== undefined) {
    return point.valeurNumerique;
  }
  if (point.valeurTexte !== null && point.valeurTexte !== undefined) {
    return point.valeurTexte;
  }
  if (point.valeurJson !== null && point.valeurJson !== undefined) {
    return point.valeurJson;
  }
  return point.valeur;
}

function pointEstRenseigne(point?: PointDeDonnees): boolean {
  if (!point) return false;
  if (point.mention || point.motifIndisponible) return true;
  const valeur = valeurDuPoint(point);
  return valeur !== null && valeur !== undefined && valeur !== '';
}
